import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import RateCardShell, { StarredRateCardBadge } from '~components/RateCard/RateCardShell';
import RateCardSummary from '~components/RateCard/RateCardSummary';
import useHoverPopover from '~hooks/useHoverPopover';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';

/**
 * Rate-card affordance for list rows. Hovering reveals every rate card that applies to the
 * row; clicking pins the popover open so the rates can be read and copied without opening
 * the edit form.
 *
 * Cards are fetched on first open, not on list render: the list payloads carry at most a
 * count of the applicable cards, not the cards themselves, and eagerly fetching per row
 * would put one request per visible row on every page load.
 */

// Clicks on the popover bubble through the portal to the host row's React ancestors —
// the same expand/collapse toggle risk as the trigger — so the paper and the backdrop
// that dismisses a pinned popover both swallow them.
const stopClickPropagation = (event: React.MouseEvent) => event.stopPropagation();

interface RateCardIndicatorProps {
  /**
   * Fetches every rate card that applies to this row. Called once, on first open; a
   * rejection renders a failure state and the next open retries.
   */
  loadRateCards: () => Promise<SiteRateSchedule[]>;
  /**
   * The card currently applied to this row, when known. Marked with a star so a dispatcher
   * can tell which of several candidate cards actually priced the work.
   */
  appliedRateCardId?: string | null;
}

export default function RateCardIndicator({
  loadRateCards,
  appliedRateCardId = null,
}: RateCardIndicatorProps) {
  const { anchorEl, isOpen, isPinned, triggerProps, popoverHandlers, close } =
    useHoverPopover({ isPinnable: true });
  const [rateCards, setRateCards] = useState<SiteRateSchedule[] | null>(null);
  const [hasFailedToLoad, setHasFailedToLoad] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // `rateCards === null` means "not fetched yet", which is distinct from a fetched-and-empty
  // result — only the former should trigger a request.
  const hasLoaded = rateCards !== null;

  const loadOnce = useCallback(async () => {
    if (hasLoaded || isLoading) {
      return;
    }

    setIsLoading(true);
    setHasFailedToLoad(false);

    try {
      setRateCards(await loadRateCards());
    } catch {
      // Error already handled (toast + Sentry) by the fetchers behind loadRateCards.
      // Keeping `rateCards` null (rather than caching an empty list) lets the next
      // open retry.
      setHasFailedToLoad(true);
    } finally {
      setIsLoading(false);
    }
  }, [hasLoaded, isLoading, loadRateCards]);

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      triggerProps.onMouseEnter(event);
      loadOnce();
    },
    [loadOnce, triggerProps],
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // Host rows toggle expand/collapse on click; pinning the popover is a read action
      // and must not double as that toggle.
      event.stopPropagation();
      triggerProps.onClick?.(event);
      loadOnce();
    },
    [loadOnce, triggerProps],
  );

  return (
    <>
      <Box
        aria-label={t('rate_card.view_rate_cards')}
        // Inline element: callers render this inside running text (the order card subtitle
        // is a <p>), where a block-level trigger would be invalid HTML.
        component="span"
        onClick={handleClick}
        onMouseEnter={handleOpen}
        onMouseLeave={triggerProps.onMouseLeave}
        // Styled as a link — blue and underlined — so it reads as interactive and invites
        // the hover that reveals the rates. Font size is inherited so each caller can match
        // its own surface's type scale; `whiteSpace: nowrap` keeps the label on one line in
        // tight rows rather than breaking into "Rates / Set".
        sx={(theme) => ({
          alignItems: 'center',
          color: theme.semantic.linkText,
          cursor: 'pointer',
          display: 'inline-flex',
          fontSize: 'inherit',
          gap: 0.25,
          textDecoration: 'underline',
          whiteSpace: 'nowrap',
          '&:hover': { textDecoration: 'none' },
        })}
      >
        {/* 1em so the icon tracks whatever font size the caller sets. */}
        <RequestQuoteOutlined sx={{ fontSize: '1em' }} />
        <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 600 }}>
          {t('rate_card.rates_set')}
        </Typography>
      </Box>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={close}
        open={isOpen}
        slotProps={{
          backdrop: { onClick: stopClickPropagation },
          paper: {
            ...popoverHandlers,
            onClick: stopClickPropagation,
            sx: (theme) => ({
              border: `1px solid ${theme.brandV2.colors.treadGray7}`,
              borderRadius: '0.25rem',
              boxShadow: theme.shadows[3],
              // Wide enough for a wrapped card name beside its condition chips without the
              // rate columns collapsing; scrolls past ~6 cards.
              maxHeight: '28rem',
              maxWidth: '32rem',
              overflowY: 'auto',
              p: 2,
              width: '28rem',
            }),
          },
        }}
        // Hover popovers must not steal focus or swallow scroll from the list behind them.
        disableRestoreFocus
        disableScrollLock
        sx={{
          ...(isPinned ? {} : { pointerEvents: 'none' }),
          '& .MuiPopover-paper': { pointerEvents: 'auto' },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      >
        <RateCardPopoverBody
          appliedRateCardId={appliedRateCardId}
          hasFailedToLoad={hasFailedToLoad}
          rateCards={rateCards}
        />
      </Popover>
    </>
  );
}

interface RateCardPopoverBodyProps {
  appliedRateCardId: string | null;
  hasFailedToLoad: boolean;
  rateCards: SiteRateSchedule[] | null;
}

function RateCardPopoverBody({
  appliedRateCardId,
  hasFailedToLoad,
  rateCards,
}: RateCardPopoverBodyProps) {
  if (hasFailedToLoad) {
    return (
      <Typography
        sx={(theme) => ({ color: theme.brandV2.colors.treadGray1, fontSize: '0.75rem' })}
      >
        {t('error_messages.rate_card.failed_to_load')}
      </Typography>
    );
  }

  // Null means the fetch has not resolved yet, which covers the loading case too.
  if (rateCards === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: '10rem', py: 1 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (rateCards.length === 0) {
    return (
      <Typography
        sx={(theme) => ({ color: theme.brandV2.colors.treadGray1, fontSize: '0.75rem' })}
      >
        {t('rate_card.no_rate_cards')}
      </Typography>
    );
  }

  // The applied card leads the list — it is the one that priced the work, and the rest are
  // context for why it won.
  const orderedRateCards = [
    ...rateCards.filter((rateCard) => rateCard.id === appliedRateCardId),
    ...rateCards.filter((rateCard) => rateCard.id !== appliedRateCardId),
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray1,
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
        })}
      >
        {t('rate_card.rate_card_count', { count: rateCards.length })}
      </Typography>

      {orderedRateCards.map((rateCard) => {
        const isApplied = rateCard.id === appliedRateCardId;

        return (
          <RateCardShell
            key={rateCard.id}
            badge={
              isApplied ? <StarredRateCardBadge label={t('rate_card.applied')} /> : null
            }
            isStarred={isApplied}
          >
            <RateCardSummary rateCard={rateCard} />
          </RateCardShell>
        );
      })}
    </Box>
  );
}
